import { FEEDBACK_URL } from '@aloft/toolkit-shared';
import { LoadingIndicator, Typography } from '@aloft/toolkit-sui';
import { useQuery } from '@tanstack/react-query';

import { useState } from 'react';

import { useCalculatePricingBreakdown } from '../../hooks/useCalculatePricingBreakdown.ts';
import { CheckoutProps, PricingBreakdown } from './types.ts';

type Props = {
  priceID: CheckoutProps['priceID'];
  isTrial: boolean;
  initialPromoCode: CheckoutProps['initialPromoCode'];
  children: (props: {
    pricingBreakdown: PricingBreakdown;
    promoCode: CheckoutProps['initialPromoCode'];
  }) => React.ReactNode;
};

/** Fetches initial pricing breakdown data for `<Checkout>`. */
export const CheckoutFetcher = (props: Props) => {
  const { priceID, isTrial, initialPromoCode, children } = props;
  const [pricingBreakdown, setPricingBreakdown] = useState<PricingBreakdown | null>(null);

  const { calculatePricingBreakdown } = useCalculatePricingBreakdown();

  const getInitialPricingBreakdown = useQuery({
    staleTime: Infinity,
    queryKey: ['initialPricingBreakdown', priceID],
    queryFn: async () => {
      const res = await calculatePricingBreakdown({
        isTrial: !!isTrial,
        priceID,
        promotionCode: initialPromoCode,
      });

      setPricingBreakdown(res);
      return res;
    },
  });

  if (getInitialPricingBreakdown.isError) {
    return (
      <Typography>
        We&apos;re sorry, we couldn&apos;t complete your order. Please reload the page and try
        again, or&nbsp;
        <a
          href={FEEDBACK_URL}
          target="_blank"
          style={{ textDecoration: 'underline', color: 'blue' }}
          rel="noreferrer"
        >
          contact us
        </a>
        &nbsp;if this issue continues.
      </Typography>
    );
  }

  if (getInitialPricingBreakdown.isLoading || !pricingBreakdown) {
    return <LoadingIndicator message="Loading pricing details..." />;
  }

  return children({
    pricingBreakdown,
    promoCode: initialPromoCode,
  });
};
